/**
 * Gemini Function Call Agent Loop — multi-turn conversation where
 * Gemini calls Cogo ActionDefs as tools and receives results back.
 *
 * The loop terminates when:
 *  - Gemini responds with text (no function call) = final answer
 *  - maxTurns is exceeded → throws error
 *  - tokenBudget is exceeded → throws error
 */

import type { GeminiClient, GeminiContent, GeminiFunctionDeclaration, GeminiPart } from './gemini-client'
import type { ToolCallbackHandler } from './tool-callback'
import { buildFunctionDeclarations } from './function-declaration'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AgentLoopOptions {
  client: GeminiClient
  toolHandler: ToolCallbackHandler
  prompt: string
  tools: GeminiFunctionDeclaration[]
  maxTurns?: number
  tokenBudget?: number
  systemPrompt?: string
  signal?: AbortSignal
}

export interface AgentLoopResult {
  text: string
  history: GeminiContent[]
}

/** Minimal action engine interface for registering the agent loop action. */
export interface AgentLoopActionEngine {
  registerAction(name: string, handler: (params: Record<string, unknown>) => void | Promise<void>): void
  state: {
    setState(path: string, value: unknown): void
    resolve(template: string): unknown
    get?(path: string): unknown
  }
  compute: { evaluate(expr: string): unknown }
}

// ---------------------------------------------------------------------------
// Agent Loop
// ---------------------------------------------------------------------------

export async function agentLoop(options: AgentLoopOptions): Promise<AgentLoopResult> {
  const {
    client,
    toolHandler,
    prompt,
    tools,
    maxTurns = 5,
    tokenBudget = 20000,
    systemPrompt,
  } = options

  const contents: GeminiContent[] = []

  // System prompt as first user message if provided
  if (systemPrompt) {
    contents.push({ role: 'user', parts: [{ text: systemPrompt }] })
    contents.push({ role: 'model', parts: [{ text: 'Understood.' }] })
  }

  contents.push({ role: 'user', parts: [{ text: prompt }] })

  const toolsPayload = tools.length > 0
    ? [{ functionDeclarations: tools }]
    : undefined

  let totalTokens = 0

  for (let turn = 0; turn < maxTurns; turn++) {
    const result = await client.generateContent({
      contents,
      ...(toolsPayload && { tools: toolsPayload }),
    })

    // Track token usage
    const usage = (result.raw as any)?.usageMetadata?.totalTokenCount
    if (typeof usage === 'number') {
      totalTokens = usage
    }

    if (totalTokens > tokenBudget) {
      throw new Error(`Agent loop exceeded token budget (${totalTokens}/${tokenBudget})`)
    }

    // Check for parallel function calls first
    const calls = result.functionCalls ?? (result.functionCall ? [result.functionCall] : [])

    if (calls.length === 0) {
      // Final text response — loop complete
      const modelContent: GeminiContent = {
        role: 'model',
        parts: [{ text: result.text ?? '' }],
      }
      contents.push(modelContent)
      return { text: result.text ?? '', history: contents }
    }

    // Push model content with all function call parts
    const modelParts: GeminiPart[] = calls.map((fc) => ({
      functionCall: { name: fc.name, args: fc.args, ...(fc.id ? { id: fc.id } : {}) },
    }))
    contents.push({ role: 'model', parts: modelParts })

    // Execute all tool calls in parallel
    const results = await Promise.all(
      calls.map((fc) => toolHandler.handle(fc)),
    )

    // Push function response content for each result
    for (let i = 0; i < results.length; i++) {
      const r = results[i]
      const id = calls[i].id
      const responsePart: GeminiPart = {
        functionResponse: {
          name: r.name,
          response: r.response,
          ...(id ? { id } : {}),
        },
      }
      contents.push({ role: 'user', parts: [responsePart] })
    }
  }

  throw new Error(`Agent loop exceeded ${maxTurns} turns without final answer`)
}

// ---------------------------------------------------------------------------
// Action Registration
// ---------------------------------------------------------------------------

function resultPath(params: Record<string, unknown>, defaultTarget: string): string {
  return String(params.resultTarget ?? defaultTarget).replace(/^#/, '')
}

/**
 * Register the `geminiFunctionCall` action on an engine.
 *
 * This action runs a multi-turn agent loop where Gemini calls
 * Cogo ActionDefs as tools and receives results back.
 */
export function registerAgentLoopAction(engine: AgentLoopActionEngine): void {
  engine.registerAction('geminiFunctionCall', async (raw) => {
    const params = raw as Record<string, unknown>
    const prompt = String(params.prompt ?? '')
    const model = String(params.model ?? 'gemini-2.5-flash')
    const apiKey = String(params.apiKey ?? '')
    const maxTurns = Number(params.maxTurns ?? 5)
    const tokenBudget = Number(params.tokenBudget ?? 20000)
    const systemPrompt = params.systemPrompt ? String(params.systemPrompt) : undefined
    const target = resultPath(params, '_results.agent')
    const toolNames = (params.tools as string[]) ?? []

    // Lazy import to avoid circular dependencies
    const { GeminiClient } = await import('./gemini-client')
    const { ToolCallbackHandler } = await import('./tool-callback')

    const client = new GeminiClient({ apiKey, model })
    const toolHandler = new ToolCallbackHandler(
      async (actionName, actionParams) => {
        // Execute action via the engine's registered action
        const action = (engine as any).builtinActions?.[actionName]
        if (action) {
          await action(actionParams)
          return engine.state.get?.(target) ?? {}
        }
        throw new Error(`Unknown action: ${actionName}`)
      },
    )

    const tools = buildFunctionDeclarations(toolNames)

    try {
      const result = await agentLoop({
        client,
        toolHandler,
        prompt,
        tools,
        maxTurns,
        tokenBudget,
        systemPrompt,
      })

      engine.state.setState(target, { data: result.text, error: null })
      engine.state.setState('_results.agentHistory', result.history)
    } catch (err: any) {
      engine.state.setState(target, { data: null, error: err.message ?? String(err) })
    }
  })
}
